import { useEffect, useState } from 'react';

import { Box, Stack, TextField } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { EditDrawer } from 'common/components/EditDrawer';
import {
    closeDrawer,
    selectCurrentDrawerId,
    selectNameInConflict,
    selectOpen,
    setNameInConflict,
    setSaveButtonEnabled,
} from 'common/components/EditDrawer/editDrawerSlice';
import { DEFAULT_ERROR_HANDLER_INLINE_STYLES } from 'common/components/errorHandler/ErrorHandler';
import { PageInfo, selectPageInfo, setRows } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { getOptionLabelDefaultCallback,isOptionEqualToValueDefaultCallback } from 'common/components/sharedCustomComponents/Common/helpers';
import { EntitiesSearchableCommonDropDown } from 'common/components/sharedCustomComponents/EntitiesSearchableCommonDropDown/EntitiesSearchableCommonDropDown';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { fetchRoles } from 'common/services/api/Roles/Roles.thunk';
import { postRoleUserGroup } from 'common/services/api/RolesUserGroup/RoleUserGroup.thunk';
import { Role } from 'common/types/Entities';
import { EditDrawerContentProps } from 'common/types/TypeDefs';
import { DrawerIDs } from 'common/utils/DrawerIDs';
import { DEFAULT_ERROR_MESSAGE } from 'common/utils/errorHandlers';
import { displayRoleOptionMenuItemContent } from 'common/utils/RoleEntitiesDropDownHelpers';
import { useSnackbar } from 'notistack';

const EXISTING_ROLE_LINKING_ERROR_MESSAGE = 'This Role is already linked to this User Group.';
const ROLE_USER_GROUP_POST_SUCCESS_MESSAGE = 'The Role was successfully linked.';

const constructCreateContentComponent = (
    title: string,
    pageInfo: PageInfo | undefined,
    saveButtonFunction: () => void,
    processing: boolean,
    role: Role | null,
    handleRoleChange: (e: unknown, role: Role | null) => void,
    nameInConflict: boolean,
) => (
    <EditDrawer
        title={title}
        saveButtonFunction={saveButtonFunction}
        processing={processing}
    >
        <Stack spacing={2}>
            <EntitiesSearchableCommonDropDown
                initialEntityOption={{ id: '', name: '', tenantId: '' }}
                value={role}
                onChange={handleRoleChange}
                label='Role Name'
                getOptionLabel={getOptionLabelDefaultCallback}
                isOptionEqualToValue={isOptionEqualToValueDefaultCallback}
                asyncThunkAction={fetchRoles}
                asyncThunkArgs={{ tenantId: pageInfo?.tenantId ?? '' }}
                displayMenuItemContent={displayRoleOptionMenuItemContent}
                hasError={nameInConflict}
                effectDependecyArray={[pageInfo?.tenantId ?? '']}
            />
            {nameInConflict &&
                <Box
                    component='div'
                    display='inline'
                    sx={DEFAULT_ERROR_HANDLER_INLINE_STYLES}
                >
                    {EXISTING_ROLE_LINKING_ERROR_MESSAGE}
                </Box>
            }
            <TextField
                sx={{
                    backgroundColor: 'background.paper',
                    borderRadius: '4px',
                }}
                size="small"
                label="User Group Name"
                variant="filled"
                defaultValue={pageInfo?.name ?? ''}
                disabled
            />
            <TextField
                sx={{
                    backgroundColor: 'background.paper',
                    borderRadius: '4px',
                }}
                size="small"
                label="Tenant Name"
                variant="filled"
                defaultValue={pageInfo?.tenantName ?? ''}
                disabled
            />
        </Stack>
    </EditDrawer>
);

export function RoleUserGroupCreateContent({
    title,
}: EditDrawerContentProps) {
    const [role, setRole] = useState<Role | null>(null);
    const [processing, setProcessing] = useState<boolean>(false);
    const pageInfo = useAppSelector(selectPageInfo);
    const nameInConflict = useAppSelector(selectNameInConflict);
    const drawerOpen = useAppSelector(selectOpen);
    const currentId = useAppSelector(selectCurrentDrawerId);
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        if(currentId === DrawerIDs.Role){
            setRole(null);
            dispatch(setSaveButtonEnabled(false));
        }
    }, [drawerOpen]);

    const handleRoleChange = (_e: unknown, newRole: Role | null) => {
        setRole(newRole);
        dispatch(setSaveButtonEnabled(!!newRole?.id));
        dispatch(setNameInConflict(false));
    };

    const roleUserGroupPostCallback = () => {
        setProcessing(true);
        const roleUserGroupOptions = {
            tenantId: pageInfo?.tenantId ?? '',
            userGroupId: pageInfo?.id ?? '',
            roleId: role?.id ?? '',
        };

        dispatch(postRoleUserGroup(roleUserGroupOptions))
            .unwrap()
            .then((result) => {
                dispatch(setNameInConflict(false));
                enqueueSnackbar(ROLE_USER_GROUP_POST_SUCCESS_MESSAGE, { variant: 'success' });
                dispatch(setRows(result.rows));
                dispatch(closeDrawer());
            })
            .catch((error) => {
                dispatch(setNameInConflict(false));
                if (extractStatusCode(error.message) === 409) {
                    dispatch(setNameInConflict(true));
                } else {
                    enqueueSnackbar(
                        'There was an error while linking the Role.' +
                        DEFAULT_ERROR_MESSAGE,
                        { variant: 'error' }
                    );
                }
            })
            .finally(() => {
                setProcessing(false);
            });
    };

    return constructCreateContentComponent(
        title,
        pageInfo,
        roleUserGroupPostCallback,
        processing,
        role,
        handleRoleChange,
        nameInConflict ?? false,
    );
}
